/**
 * TanStack Query wrappers for the administration screens.
 *
 * Toast copy is read from the `settings` namespace here rather than in the
 * pages, so every mutation reports itself the same way.
 */
import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MAX_PAGE_SIZE } from '@nova/shared';
import { localized, useLang } from '@/i18n';
import { errorMessage } from '@/lib/api';
import {
  auditApi,
  auditKeys,
  filesApi,
  holidayKeys,
  holidaysApi,
  procedureCategoriesApi,
  procedureCategoryKeys,
  referenceListKeys,
  referenceListsApi,
  roleKeys,
  rolesApi,
  settingsApi,
  settingsKeys,
  userKeys,
  usersApi,
} from './api';
import type {
  AuditQuery,
  CalendarSettingsPatch,
  ClinicSettingsPatch,
  CreateUserBody,
  FlatProcedureCategory,
  HolidayBody,
  HolidayQuery,
  MatrixGroup,
  MatrixResourceRow,
  PermissionCatalog,
  ProcedureCategoryNode,
  ReferenceItemBody,
  RoleBody,
  RoleQuery,
  UpdateUserBody,
  UserQuery,
} from './types';

/** Every mutation reports failures with the API's own Arabic message. */
function useToasts() {
  const { t } = useTranslation('settings');
  return {
    ok: (key: string) => toast.success(t(key)),
    fail: (error: unknown) => toast.error(errorMessage(error)),
  };
}

// ---------------------------------------------------------------------------
// Clinic settings
// ---------------------------------------------------------------------------

export function useClinicSettings(enabled = true) {
  return useQuery({
    queryKey: settingsKeys.clinic(),
    queryFn: settingsApi.get,
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateClinicSettings() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: (body: ClinicSettingsPatch) => settingsApi.update(body),
    onSuccess: (data) => {
      qc.setQueryData(settingsKeys.clinic(), data);
      void qc.invalidateQueries({ queryKey: settingsKeys.all });
      ok('toast.settingsSaved');
    },
    onError: fail,
  });
}

/**
 * DS-02 — the clinic's own name, for the chrome that carries it.
 *
 * Read from `/settings/client-config` rather than `/settings`, which is behind
 * SETTINGS_MANAGE: the title bar renders for a receptionist too. Empty until an
 * admin has actually filled the name in, and the caller then shows nothing —
 * a stand-in name would be some other clinic's.
 */
export function useClinicName(enabled = true): string {
  const { data } = useQuery({
    queryKey: settingsKeys.clientConfig(),
    queryFn: settingsApi.clientConfig,
    enabled,
    staleTime: 10 * 60_000,
  });
  return data?.clinic?.name?.trim() ?? '';
}

/**
 * The clinic logo doubles as the browser-tab icon. Read from `client-config`
 * for the same reason as the name — every signed-in user sees the tab. Until a
 * logo is uploaded (or while signed out) the bundled `/favicon.svg` stays.
 */
export function useClinicFavicon(enabled = true): void {
  const { data } = useQuery({
    queryKey: settingsKeys.clientConfig(),
    queryFn: settingsApi.clientConfig,
    enabled,
    staleTime: 10 * 60_000,
  });
  const logoUrl = useLogoUrl(enabled ? data?.clinic?.logoFileId : null).data;

  useEffect(() => {
    if (!logoUrl) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    const original = { href: link.href, type: link.type };
    link.href = logoUrl;
    // The bundled icon is declared as SVG; an uploaded PNG/JPEG must not be.
    link.removeAttribute('type');
    return () => {
      link.href = original.href;
      link.type = original.type;
    };
  }, [logoUrl]);
}

/** The logo lives behind a bearer-guarded route; render it from a blob URL. */
export function useLogoUrl(fileId: string | null | undefined) {
  return useQuery({
    queryKey: settingsKeys.logo(fileId ?? ''),
    queryFn: () => filesApi.blobUrl(fileId as string),
    enabled: Boolean(fileId),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useUploadLogo() {
  const { fail } = useToasts();
  return useMutation({ mutationFn: (file: File) => filesApi.upload(file), onError: fail });
}

// ---------------------------------------------------------------------------
// Calendar settings (CAL-01/02/12)
// ---------------------------------------------------------------------------

export function useCalendarSettings() {
  return useQuery({
    queryKey: settingsKeys.calendar(),
    queryFn: settingsApi.getCalendar,
    staleTime: 60_000,
  });
}

export function useUpdateCalendarSettings() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: (body: CalendarSettingsPatch) => settingsApi.updateCalendar(body),
    onSuccess: (data) => {
      qc.setQueryData(settingsKeys.calendar(), data);
      void qc.invalidateQueries({ queryKey: settingsKeys.all });
      ok('toast.settingsSaved');
    },
    onError: fail,
  });
}

// ---------------------------------------------------------------------------
// Holidays (CAL-12)
// ---------------------------------------------------------------------------

export function useHolidays(params: HolidayQuery) {
  return useQuery({
    queryKey: holidayKeys.list(params),
    queryFn: () => holidaysApi.list(params),
  });
}

export function useSaveHoliday() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: HolidayBody }) =>
      id ? holidaysApi.update(id, body) : holidaysApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: holidayKeys.all });
      ok('toast.holidaySaved');
    },
    onError: fail,
  });
}

export function useDeactivateHoliday() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: (id: string) => holidaysApi.deactivate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: holidayKeys.all });
      ok('toast.holidayDeactivated');
    },
    onError: fail,
  });
}

// ---------------------------------------------------------------------------
// Reference lists (PRD §5.10)
// ---------------------------------------------------------------------------

export function useReferenceListKeys() {
  return useQuery({
    queryKey: referenceListKeys.keys(),
    queryFn: referenceListsApi.keys,
    staleTime: Infinity,
  });
}

/**
 * Inactive rows are fetched too and filtered in the browser: drag-to-reorder
 * posts the whole list back, and a partially loaded list would be rejected as
 * a reorder mismatch.
 */
export function useReferenceItems(listKey: string | null) {
  return useQuery({
    queryKey: referenceListKeys.list(listKey ?? ''),
    queryFn: () =>
      referenceListsApi.list(listKey as string, { pageSize: MAX_PAGE_SIZE, isActive: 'all' }),
    enabled: Boolean(listKey),
  });
}

export function useSaveReferenceItem(listKey: string) {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: ReferenceItemBody }) =>
      id
        ? referenceListsApi.update(listKey, id, body)
        : referenceListsApi.create(listKey, body as ReferenceItemBody),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: referenceListKeys.list(listKey) });
      ok('toast.itemSaved');
    },
    onError: fail,
  });
}

export function useReorderReferenceItems(listKey: string) {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: (orderedIds: string[]) => referenceListsApi.reorder(listKey, orderedIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: referenceListKeys.list(listKey) });
      ok('toast.reordered');
    },
    onError: (error) => {
      // Put the rows back where the server still thinks they are.
      void qc.invalidateQueries({ queryKey: referenceListKeys.list(listKey) });
      fail(error);
    },
  });
}

export function useDeactivateReferenceItem(listKey: string) {
  const qc = useQueryClient();
  const { fail } = useToasts();
  const { t } = useTranslation('settings');
  return useMutation({
    mutationFn: (id: string) => referenceListsApi.deactivate(listKey, id),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: referenceListKeys.list(listKey) });
      toast.success(result.deleted ? t('toast.itemDeleted') : t('toast.itemDeactivated'));
    },
    onError: fail,
  });
}

export function useMergeAllergy() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      referenceListsApi.merge('ALLERGY', sourceId, targetId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: referenceListKeys.list('ALLERGY') });
      void qc.invalidateQueries({ queryKey: ['patients'] });
      void qc.invalidateQueries({ queryKey: ['medications'] });
      ok('toast.allergyMerged');
    },
    onError: fail,
  });
}

// ---------------------------------------------------------------------------
// Users (SEC-05)
// ---------------------------------------------------------------------------

export function useUsers(params: UserQuery, enabled = true) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => usersApi.list(params),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: (body: CreateUserBody) => usersApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
      void qc.invalidateQueries({ queryKey: roleKeys.all });
      ok('toast.userCreated');
    },
    onError: fail,
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserBody }) => usersApi.update(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
      void qc.invalidateQueries({ queryKey: roleKeys.all });
      ok('toast.userSaved');
    },
    onError: fail,
  });
}

export function useResetPassword() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: ({
      id,
      newPassword,
      mustChangePassword,
    }: {
      id: string;
      newPassword?: string;
      mustChangePassword?: boolean;
    }) => usersApi.resetPassword(id, { newPassword, mustChangePassword }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
      ok('toast.passwordReset');
    },
    onError: fail,
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: ({ id, active, reason }: { id: string; active: boolean; reason?: string }) =>
      active ? usersApi.activate(id) : usersApi.deactivate(id, reason),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
      ok(variables.active ? 'toast.userActivated' : 'toast.userDeactivated');
    },
    onError: fail,
  });
}

// ---------------------------------------------------------------------------
// Roles and the permission matrix (SEC-01)
// ---------------------------------------------------------------------------

export function useRoles(
  params: RoleQuery = { pageSize: MAX_PAGE_SIZE, isActive: 'all' },
  enabled = true,
) {
  return useQuery({ queryKey: roleKeys.list(params), queryFn: () => rolesApi.list(params), enabled });
}

export function usePermissionCatalog() {
  return useQuery({
    queryKey: roleKeys.permissions(),
    queryFn: rolesApi.permissions,
    staleTime: Infinity,
  });
}

export function useSaveRole() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: RoleBody }) =>
      id ? rolesApi.update(id, body) : rolesApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleKeys.all });
      void qc.invalidateQueries({ queryKey: userKeys.all });
      ok('toast.roleSaved');
    },
    onError: fail,
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  const { ok, fail } = useToasts();
  return useMutation({
    mutationFn: (id: string) => rolesApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleKeys.all });
      ok('toast.roleDeleted');
    },
    onError: fail,
  });
}

/** SEC-02 — the category tree, flattened for the scope checkbox list. */
export function useProcedureCategoryOptions() {
  const query = useQuery({
    queryKey: procedureCategoryKeys.tree(),
    queryFn: procedureCategoriesApi.tree,
    staleTime: 300_000,
  });

  const options = useMemo<FlatProcedureCategory[]>(() => {
    const out: FlatProcedureCategory[] = [];
    const walk = (nodes: ProcedureCategoryNode[]): void => {
      for (const node of nodes) {
        out.push({
          id: node.id,
          nameAr: node.nameAr,
          nameEn: node.nameEn,
          depth: node.depth,
          color: node.color,
        });
        if (node.children?.length) walk(node.children);
      }
    };
    walk(query.data ?? []);
    return out;
  }, [query.data]);

  return { ...query, options };
}

/**
 * SEC-01 — turn the flat permission catalogue into the matrix the screen draws.
 *
 * Keys are `<domain>.<resource>.<action>`, so the resource is a row and the
 * action is a column. Anything that is not one of the four CRUD verbs (merge,
 * post, override, checkIn…) cannot be a column without inventing thirty of
 * them, so it renders as a named checkbox in the row's "other" cell.
 */
export const MATRIX_ACTIONS = ['view', 'create', 'update', 'delete', 'manage'] as const;

export function buildMatrix(catalog: PermissionCatalog | undefined): MatrixGroup[] {
  if (!catalog) return [];
  return catalog.groups.map((group) => {
    const rows = new Map<string, MatrixResourceRow>();
    for (const key of group.permissions) {
      const parts = key.split('.');
      const resource = parts.length >= 3 ? parts[1] : parts[0];
      const action = parts[parts.length - 1];
      const row = rows.get(resource) ?? { resource, byAction: {}, extras: [] };
      if ((MATRIX_ACTIONS as readonly string[]).includes(action)) row.byAction[action] = key;
      else row.extras.push({ action, key });
      rows.set(resource, row);
    }
    return { ...group, rows: [...rows.values()] };
  });
}

// ---------------------------------------------------------------------------
// Audit log (SEC-04)
// ---------------------------------------------------------------------------

export function useAuditLog(params: AuditQuery) {
  return useQuery({
    queryKey: auditKeys.list(params),
    queryFn: () => auditApi.list(params),
    placeholderData: (previous) => previous,
  });
}

/**
 * The entity filter offers every entity the log holds, not just the ones on the
 * newest page — an entity nobody has touched in weeks is exactly what someone
 * filtering an old change is looking for. Already sorted by Arabic label.
 */
export function useAuditEntityOptions() {
  const lang = useLang();
  const query = useQuery({
    queryKey: auditKeys.entities(),
    queryFn: () => auditApi.entities(),
    staleTime: 300_000,
  });

  const options = useMemo(
    () =>
      (query.data ?? []).map((row) => ({
        value: row.entity,
        label: localized(lang, row.entityLabelAr, row.entityLabelEn),
      })),
    [query.data, lang],
  );

  return { ...query, options };
}
